<template>
  <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
    <p class="tw-text-sm tw-text-gray-500">
      {{ $t('subscriptions.plans.hint', 'Synced from WooCommerce. Role & period are edited in WordPress.') }}
    </p>
    <a-button intent="flat" left-icon="solar:refresh-bold-duotone" @click="load(pagination.current_page)" :loading="loading">
      {{ $t('common.refresh', 'Refresh') }}
    </a-button>
  </div>

  <div class="tw-bg-white tw-rounded-lg tw-shadow-smx tw-border tw-overflow-hidden tw-border-gray-200">
  <div class="tw-rounded tw-overflow-auto tw-border tw-border-solid tw-border-gray-100">
    <table class="tw-min-w-full tw-leading-normal">
      <thead>
        <tr>
          <th v-for="c in columns" :key="c.label" :class="c.classes"
            class="tw-px-5 last:tw-border-e-0 tw-whitespace-nowrap tw-border-e tw-py-3 tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-text-[12px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
            {{ $t(c.label, c.fallback) }}
          </th>
        </tr>
      </thead>

      <tbody v-if="loading">
        <tr v-for="i in 10" :key="i" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50">
          <td v-for="c in columns" :key="c.label" class="tw-px-2 tw-py-3">
            <div class="tw-w-full tw-py-3 tw-bg-gray-50 group-even:tw-bg-white tw-rounded tw-min-w-[30px] tw-animate-pulse"></div>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <template v-if="items.length">
          <tr v-for="p in items" :key="p.id" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 tw-text-sm">
            <td class="tw-px-5 tw-py-3">
              <div class="tw-font-semibold tw-text-gray-800">{{ p.name }}</div>
              <div class="tw-text-xs tw-text-gray-400">#{{ p.woo_product_id }}<span v-if="p.woo_variation_id">/{{ p.woo_variation_id }}</span></div>
            </td>
            <td class="tw-px-5 tw-py-3 tw-capitalize">{{ p.role }}</td>
            <td class="tw-px-5 tw-py-3">{{ p.duration_count }} {{ p.duration_unit }}{{ p.duration_count > 1 ? 's' : '' }}</td>
            <td class="tw-px-5 tw-py-3">{{ p.price ? (p.price + ' ' + (p.currency || '')) : '—' }}</td>
            <td class="tw-px-5 tw-py-3">
              <span :class="p.is_active ? 'tw-bg-green-100 tw-text-green-700' : 'tw-bg-gray-100 tw-text-gray-500'" class="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold">
                {{ p.is_active ? $t('common.active', 'Active') : $t('common.inactive', 'Inactive') }}
              </span>
            </td>
            <td class="tw-px-5 tw-py-3 tw-text-end tw-whitespace-nowrap">
              <button @click="toggle(p)" class="tw-text-primary-500 hover:tw-text-primary-600 tw-text-xs tw-font-semibold tw-mr-3">
                {{ p.is_active ? $t('common.disable', 'Disable') : $t('common.enable', 'Enable') }}
              </button>
              <button @click="remove(p)" class="tw-text-red-500 hover:tw-text-red-600 tw-text-xs tw-font-semibold">
                {{ $t('common.delete', 'Delete') }}
              </button>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td :colspan="columns.length">
              <div class="tw-p-5 tw-bg-white tw-min-h-[250px] tw-text-gray-600 tw-flex tw-items-center tw-justify-center tw-text-lg">
                <p>{{ $t('subscriptions.plans.empty', 'No plans synced yet.') }}</p>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  </div>

  <div class="tw-mt-2">
    <TablePagination v-if="items.length" :has-next="!!pagination.next_page_url" :has-previous="!!pagination.prev_page_url" @previous="onPrevious" @next="onNext" />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import TablePagination from '@/components/table/TablePagination';
import subscriptionsApi from '@/api/subscriptions';
import useAlert from '@/composables/useAlert';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const items = ref([]);
const loading = ref(false);
const pagination = reactive({ current_page: 1, last_page: 1, total: 0, next_page_url: null, prev_page_url: null });

const columns = [
  { label: 'subscriptions.plans.product', fallback: 'Product', classes: [] },
  { label: 'subscriptions.plans.role', fallback: 'Role', classes: ['tw-w-[120px]'] },
  { label: 'subscriptions.plans.period', fallback: 'Period', classes: ['tw-w-[140px]'] },
  { label: 'subscriptions.plans.price', fallback: 'Price', classes: ['tw-w-[120px]'] },
  { label: 'subscriptions.plans.status', fallback: 'Status', classes: ['tw-w-[110px]'] },
  { label: 'common.actions', fallback: 'Actions', classes: ['tw-w-[160px]'] },
];

const load = async (page = 1) => {
  loading.value = true;
  items.value = [];
  try {
    const res = await subscriptionsApi.admin.plans({ page });
    const d = res.data.data;
    items.value = d.data || [];
    Object.assign(pagination, { current_page: d.current_page, last_page: d.last_page, total: d.total, next_page_url: d.next_page_url, prev_page_url: d.prev_page_url });
  } catch (e) {
    useAlert(t('errors.something-wrong-happened'), 'danger');
  } finally {
    loading.value = false;
  }
};

const onNext = () => { if (pagination.current_page < pagination.last_page) load(pagination.current_page + 1); };
const onPrevious = () => { if (pagination.current_page > 1) load(pagination.current_page - 1); };

const toggle = async (p) => {
  try { await subscriptionsApi.admin.togglePlan(p.id); p.is_active = !p.is_active; }
  catch (e) { useAlert(t('errors.something-wrong-happened'), 'danger'); }
};

const remove = async (p) => {
  if (!confirm(t('subscriptions.plans.confirm-delete', 'Delete this plan from the local list?'))) return;
  try { await subscriptionsApi.admin.deletePlan(p.id); items.value = items.value.filter(i => i.id !== p.id); }
  catch (e) { useAlert(t('errors.something-wrong-happened'), 'danger'); }
};

onMounted(() => load());
</script>
